import { createState } from "@/global/store/zustand_store";
import { useAddRecipientModal } from "./AddRecipientModal";
import { TAddRecipientFormData, TRecipient, TTransferDetails } from "./types";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useApiRoutes } from "@/global/hooks/useApiRoutes";
import { errorToast } from "@/global/utils/errorToast";
import { toast } from "react-toastify";
import { useUpdateRecipientStatusModal } from "./UpdateRecipientStatusModal";
import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import { useGenericModal } from "@/shared/common/GenericModal";
import { Box } from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import { authOriginUIDs } from "@/global/config/constants";
import { routes } from "@/global/routes";
import { throttleTime } from "@/global/utils/utils";

const useTransferDetailsState = createState({} as TTransferDetails);

export function useTicketTransfer() {
  const { md: mdUp } = useCssMediaQuery("up");
  const { teamSchema, defaultRoute } = useTeamConfig();
  const router = useRouterExtended();
  const { set: setTicketTransferState, ...state } = useTransferDetailsState();
  const user = useAppStore(selectUser);
  const addRecipientModal = useAddRecipientModal();
  const tmConnectModal = useGenericModal();
  const { handleConnectTm } = useTicketmaster();

  // apis
  const {
    addRecipientApi,
    getTicketTransferDetailsApi,
    updateRecipientSeatCountApi,
    updateRecipientStatusApi,
  } = useApiRoutes();

  const resetSeatCountLoading = () => {
    setTicketTransferState({ seatCountUpdatingId: null });
  };

  const onClickAddRecipient = () => {
    addRecipientModal.set({ isOpen: true });
  };

  const getTicketTransferDetails = async () => {
    const { orderNumber, eventCode } = router?.query;
    const { data, error } = await getTicketTransferDetailsApi.post({
      teamSchema,
      orderNumber,
      eventCode,
    });
    if (error) return errorToast(error);
    setTicketTransferState(data);
    return data;
  };

  const onAddRecipientSubmit = async (formData: TAddRecipientFormData) => {
    const { orderNumber, eventCode } = router?.query;
    const payload = { teamSchema, orderNumber, eventCode, ...formData };
    const { data, error } = await addRecipientApi.post(payload);
    if (error) return errorToast(error);
    setTicketTransferState(data);
    toast.success("Transfer Initiated");
  };

  const onChangeSeatCount = async (recipient: TRecipient) => {
    setTicketTransferState({ seatCountUpdatingId: recipient.id });
    const { orderNumber, eventCode } = router?.query;
    const payload = {
      teamSchema,
      orderNumber,
      eventCode,
      id: recipient.id,
      seatCount: recipient.seatCount,
    };
    let { data, error } = await updateRecipientSeatCountApi.post(payload);
    resetSeatCountLoading();
    if (error) return errorToast(error);
    setTicketTransferState(data);
  };

  const onUpdateStatus = async (recipient: TRecipient) => {
    const { orderNumber, eventCode } = router?.query;
    const { data, error } = await updateRecipientStatusApi.post({
      teamSchema,
      orderNumber,
      eventCode,
      id: recipient.id,
      statusCode: recipient.status.code,
    });
    if (error) return errorToast(error);
    setTicketTransferState(data);
    toast.success("Recipient status updated successfully");
  };

  const fireTicketMasterModal = () => {
    const orderNumber = router?.query?.orderNumber;
    const eventCode = router?.query?.eventCode;

    tmConnectModal.set({
      isOpen: true,
      title: "Connect Ticketmaster",
      hideTitleIcon: true,
      description: (
        <Box>
          Please connect your Ticketmaster account to continue with the
          transfer.
        </Box>
      ),
      confirmButtonText: "Connect Ticketmaster",
      confirmButtonProps: { fullWidth: mdUp ? false : true },
      isCloseButtonHidden: true,
      onClickConfirm: async () => {
        await handleConnectTm({
          callback: {
            authOrigin: authOriginUIDs.ticketTransferPage,
            pathname: routes.ticket_transfer,
            query: { orderNumber, eventCode },
          },
        });
        await throttleTime(1000);
      },
      onClickClose: () => {
        router.pushSchema(defaultRoute);
      },
    });
  };

  const onLoadTicketTransferModule = async () => {
    const orderNumber = router?.query?.orderNumber;
    const eventCode = router?.query?.eventCode;

    if (!orderNumber || !eventCode) {
      return await router.pushSchema(defaultRoute);
    } else if (orderNumber && eventCode && !user) {
      return fireTicketMasterModal();
    }

    const transferDetails = await getTicketTransferDetails();
    if (!transferDetails?.orderNumber) {
      return setTicketTransferState({ isIncompletePurchase: true });
    }
  };

  return {
    ...state,
    getTicketTransferDetails,
    onClickAddRecipient,
    onAddRecipientSubmit,
    onChangeSeatCount,
    onUpdateStatus,
    onLoadTicketTransferModule,
  };
}
